import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { Plate, usePlateEditor } from 'platejs/react';
import type { Value } from 'platejs';
import { cn } from '~/lib/utils';
import { editorPlugins } from './plugins/editor-plugins';
import { EditorToolbar } from './ui/toolbar';
import { EditorContent } from './ui/editor-content';
import { serializeToHtml } from './utils/html-serializer';
import { normalizeListHtml } from './utils/normalize-list-html';

type PlateEditorProps = {
    initialValue?: string;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
};

export type PlateEditorRef = {
    getHtml: () => string;
    isEmpty: () => boolean;
    reset: () => void;
    appendHtml: (html: string) => void;
};

const defaultValue: Value = [
    {
        type: 'p',
        children: [{ text: '' }],
    },
];

export const PlateEditor = forwardRef<PlateEditorRef, PlateEditorProps>(
    (
        {
            initialValue,
            disabled = false,
            placeholder = 'Text eingeben...',
            className,
        },
        ref
    ) => {
        // Create editor with initial value (HTML string or default)
        const normalizedInitialValue = useMemo(
            () => (initialValue ? normalizeListHtml(initialValue) : undefined),
            [initialValue]
        );
        const editor = usePlateEditor({
            plugins: editorPlugins,
            value: normalizedInitialValue || defaultValue,
        });

        if (initialValue) {
            for (let i = 0; i < editor.children.length; i++) {
                const node = editor.children[i] as any;
                if ('text' in node) {
                    editor.children[i] = { type: 'p', children: [node] } as any;
                }
            }
        }

        // Expose methods to parent via ref
        useImperativeHandle(
            ref,
            () => ({
                getHtml: () => {
                    const slateValue = editor.children;

                    // Check if editor is empty
                    const isEmpty =
                        slateValue.length === 1 &&
                        slateValue[0].type === 'p' &&
                        slateValue[0].children?.length === 1 &&
                        slateValue[0].children[0].text === '';

                    if (isEmpty) {
                        return '';
                    }

                    return serializeToHtml(slateValue);
                },
                isEmpty: () => {
                    const slateValue = editor.children;
                    return (
                        slateValue.length === 1 &&
                        slateValue[0].type === 'p' &&
                        slateValue[0].children?.length === 1 &&
                        slateValue[0].children[0].text === ''
                    );
                },
                reset: () => {
                    editor.tf.reset();
                },
                appendHtml: (html: string) => {
                    if (!html) return;
                    const nodes = editor.api.html.deserialize({ element: normalizeListHtml(html) });
                    if (nodes && nodes.length > 0) {
                        const blockNodes = nodes.map((node: any) =>
                            'text' in node ? { type: 'p', children: [node] } : node
                        );
                        editor.tf.insertNodes(blockNodes, {
                            at: [editor.children.length],
                        });
                    }
                },
            }),
            [editor]
        );

        return (
            <div
                className={cn(
                    'rounded-md border border-input bg-background',
                    disabled && 'opacity-50 cursor-not-allowed',
                    className
                )}
            >
                <Plate editor={editor}>
                    <EditorToolbar disabled={disabled} />
                    <EditorContent placeholder={placeholder} disabled={disabled} />
                </Plate>
            </div>
        );
    }
);

PlateEditor.displayName = 'PlateEditor';

export default PlateEditor;
